import { DateTime } from 'luxon';

import {
  CheckMetadata,
  createSimpleCheckMetadataArray,
} from 'dashboard/components/ChecksTable/computeCheckMetadata';
import { formatDate } from 'dashboard/lib/utils/formatters';
import { CheckStatus, DocumentRecencyDetection } from 'dashboard/models/check';
import { ComparisonDirection } from 'dashboard/models/verification-check-config/document-recency-detection';

export const computeDocumentRecencyDetectionMetadata = (
  check: DocumentRecencyDetection,
): CheckMetadata => {
  const {
    'document-issue-date': documentIssueDate,
    'recency-check-date': recencyCheckDate,
    'required-recency-days': requiredRecencyDays,
    'comparison-direction': comparisonDirection,
  } = check.metadata || {};

  let acceptedUntilDate;

  if (comparisonDirection === ComparisonDirection.OlderThan) {
    if (recencyCheckDate && (requiredRecencyDays || requiredRecencyDays === 0)) {
      acceptedUntilDate = DateTime.fromISO(recencyCheckDate)
        .plus({ days: requiredRecencyDays })
        .toISO();
    }
  } else {
    if (recencyCheckDate && (requiredRecencyDays || requiredRecencyDays === 0)) {
      acceptedUntilDate = DateTime.fromISO(recencyCheckDate)
        // TODO(Documents): Backfill templates / handle when required recency days are negative
        .minus({ days: requiredRecencyDays })
        .toISO();
    }
  }

  // If check passes when submitted date is 1 day before accepted until date, then server included a
  // buffer to account for timezone difference between client time and server UTC time. We show
  // a special message to explain this! See https://en.wikipedia.org/wiki/Anywhere_on_Earth.
  const passedWithAnywhereOnEarthBuffer =
    check.status === CheckStatus.Passed &&
    documentIssueDate &&
    acceptedUntilDate &&
    Math.abs(
      Math.ceil(
        DateTime.fromISO(documentIssueDate).diff(DateTime.fromISO(acceptedUntilDate), 'days').days,
      ),
    ) <= 1;

  let acceptedUntilHelpIconMessage;
  if (requiredRecencyDays || requiredRecencyDays === 0) {
    const requiredRecencyMessage = `Includes a required recency of ${requiredRecencyDays} day(s)`;

    // Show message explaining that check passed due to Anywhere on Earth buffer.
    let passedWithAnywhereOnEarthBufferMessage;
    if (passedWithAnywhereOnEarthBuffer) {
      passedWithAnywhereOnEarthBufferMessage = 'Accepted until this date Anywhere on Earth (AoE).';
    }

    acceptedUntilHelpIconMessage = [requiredRecencyMessage, passedWithAnywhereOnEarthBufferMessage]
      .filter(Boolean)
      .join('. ');
  }

  const metadata: {
    label: string;
    value: string;
    labelHelpText?: string;
  }[] = [
    {
      label: 'Document Issue Date',
      value: formatDate(documentIssueDate) || '-',
    },
    {
      label: 'Check Run Date',
      value: formatDate(recencyCheckDate) || '-',
    },
  ];

  if (acceptedUntilDate) {
    metadata.push({
      label: 'Accepted Until',
      value: `${formatDate(acceptedUntilDate) || '-'}${passedWithAnywhereOnEarthBuffer ? ' (AoE)' : ''}`,
      labelHelpText: acceptedUntilHelpIconMessage || undefined,
    });
  } else {
    metadata.push({
      label: 'Required Recency',
      value: requiredRecencyDays ? `${requiredRecencyDays} day(s)` : '-',
    });
  }

  return {
    metadata: createSimpleCheckMetadataArray(metadata),
    hideIfVerificationPassed: false,
  };
};
